import { FC, useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { capitalizeFirstLetter } from "../../../helpers/utils";
import TextEditor from "../TextEditor";
import Checkboxes from "../Checkboxes";
import ImageField from "../ImageField";
import TimePickerValue from "../../Dashboard/TimePickerValue/TimePickerValue";
import { useSelector } from "react-redux";
import { Languages, Pages } from "../../../constants/enums";
import { FormControlLabel } from "@mui/material";
import InputNestedKeyValue from "../InputNestedKeyValue";

interface Props {
  name: string;
  label: string;
  type: string;
  autoFocus: boolean;
  defaultValue: any;
  register: any;
  setValue: any;
  errors: any;
  control: any;
  options?: any;
  disabled?: boolean;
  hidden?: any;
  resource?: any;
  slug?: any;
}

const FormField: FC<Props> = ({
  name,
  label,
  type,
  autoFocus,
  defaultValue,
  register,
  setValue,
  errors,
  control,
  options,
  disabled,
  hidden,
  resource,
  slug,
}) => {
  const language = useSelector((state: any) => state.accounts.language);

  const [currentValue, setCurrentValue] = useState<any>(defaultValue ?? "");
  useEffect(() => {
    if (type === "multiLanguages" || type === "multiLanguagesImage") {
      let multiLanguages = new Map(
        JSON.parse(resource?.multiLanguages ?? "[[0,0]]")
      );
      setCurrentValue(
        language === Languages.ENGLISH || !language
          ? defaultValue ?? ""
          : multiLanguages.get(`${language}-${name}`) ?? ""
      );
      setValue(
        name,
        language === Languages.ENGLISH || !language
          ? defaultValue ?? ""
          : multiLanguages.get(`${language}-${name}`) ?? ""
      );
    }
  }, [language, resource]);
  // console.log({slug})
  const renderField = () => {
    switch (type) {
      case "multiLanguages":
        return (
          <TextField
            fullWidth
            size="small"
            autoFocus={autoFocus}
            value={currentValue}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setCurrentValue(event.target.value);
              setValue(name, event.target.value);
            }}
            placeholder={capitalizeFirstLetter(name)}
            error={errors[name] ? true : false}
            disabled={disabled}
            hidden={hidden}
          />
        );
      case "textField":
        return (
          <TextField
            fullWidth
            size="small"
            autoFocus={autoFocus}
            defaultValue={defaultValue}
            placeholder={
              slug && slug === Pages.CONCEPTS
                ? label
                : capitalizeFirstLetter(name)
            }
            {...register(name)}
            error={errors[name] ? true : false}
            disabled={disabled}
            hidden={hidden}
          />
        );
      case "password":
        return (
          <TextField
            fullWidth
            size="small"
            type="password"
            autoFocus={autoFocus}
            placeholder={capitalizeFirstLetter(name)}
            defaultValue={defaultValue}
            {...register(name)}
            error={errors[name] ? true : false}
            disabled={disabled}
            hidden={hidden}
          />
        );
      case "number":
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            defaultValue={defaultValue}
            placeholder={capitalizeFirstLetter(name)}
            {...register(name)}
            error={errors[name] ? true : false}
            inputProps={{ min: 0 }}
            disabled={disabled}
            hidden={hidden}
          />
        );
      case "phoneNumber":
        return (
          <TextField
            fullWidth
            size="small"
            type="tel"
            defaultValue={defaultValue}
            placeholder={capitalizeFirstLetter(name)}
            {...register(name)}
            error={errors[name] ? true : false}
            disabled={disabled}
            hidden={hidden}
          />
        );
      case "date":
        return (
          <TextField
            fullWidth
            size="small"
            type="date"
            defaultValue={defaultValue}
            placeholder={capitalizeFirstLetter(name)}
            {...register(name)}
            error={errors[name] ? true : false}
            disabled={disabled}
            hidden={hidden}
          />
        );
      case "select":
        return options ? (
          <TextField
            select
            fullWidth
            size="small"
            autoFocus={autoFocus}
            defaultValue={defaultValue}
            {...register(name)}
            error={errors[name] ? true : false}
            disabled={disabled}
            hidden={hidden}
          >
            {options.map((option: any) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ) : null;
      case "checkboxes":
        return options ? (
          <Checkboxes
            label={label}
            name={name}
            options={options}
            register={register}
            defaultValue={defaultValue}
          />
        ) : null;
      case "checkbox":
        return (
          <FormControlLabel
            sx={{ mt: -1, marginLeft: "-0px" }}
            control={
              <Checkbox
                defaultChecked={defaultValue}
                {...register(name)}
                disabled={disabled}
                hidden={hidden}
              />
            }
            label={label}
            labelPlacement="start"
          />
        );
      case "autocomplete":
        return (
          <Autocomplete
            defaultValue={defaultValue}
            onChange={(e: any, value: string) => setValue(name, value)}
            isOptionEqualToValue={(option: any, value: any) =>
              option.id === value.id
            }
            options={options}
            disabled={disabled}
            getOptionLabel={(option: any) => option.label}
            renderInput={(params) => <TextField {...params} size="small" />}
            hidden={hidden}
          />
        );
      case "editor":
        return (
          <TextEditor
            initialValue={defaultValue}
            control={control}
            name={name}
          />
        );
      case "time":
        return (
          <Box sx={{ mt: -1 }}>
            <TimePickerValue
              name={name}
              defaultValue={defaultValue}
              setValue={setValue}
              register={register}
            />
          </Box>
        );
      case "image":
        return (
          <ImageField
            label={label}
            name={name}
            value={defaultValue}
            setValue={setValue}
            register={register}
            resource={resource}
            slug={slug}
          />
        );
      case "multiLanguagesImage":
        return (
          <ImageField
            label={label}
            name={name}
            value={currentValue}
            setValue={setValue}
            register={register}
            resource={resource}
            slug={slug}
            setMultiLanguageValue={setCurrentValue}
          />
        );
      case "nestedInputKeyValue":
        return (
          <InputNestedKeyValue
            label={label}
            name={name}
            value={currentValue}
            setValue={setValue}
            register={register}
            resource={resource}
            slug={slug}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      {type !== "checkboxes" &&
        type !== "checkbox" &&
        type !== "image" &&
        !hidden && (
          <Typography
            component={"label"}
            htmlFor={name}
            sx={{ mb: "4px", display: "inline-block" }}
          >
            {label}
          </Typography>
        )}
      {!hidden && renderField()}
      <Typography color="error" sx={{ fontSize: 14 }}>
        {errors[name]?.message}
      </Typography>
    </Box>
  );
};

export default FormField;
